import React, { FC, useEffect, useState } from "react";
import { Drawer, Table, Button, Input } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { failedNotification, successfulNotification } from "common/utils/notifications";
import PermissionWrapper from "shared/components/PermissionWrapper";

interface HistoryEntry {
  version: number;
  value: any;
  isActive: boolean
  updatedAt: string;
  reason?: string;
}

interface Props {
  hasEditAccess: boolean;
  settingKey: string;
  visible: boolean;
  onClose: () => void;
  apiClient: any;
  reloadParent: () => void;
}

const SettingHistoryDrawer: FC<Props> = ({
  hasEditAccess,
  settingKey,
  visible,
  onClose,
  apiClient,
  reloadParent,
}) => {
  const [data, setData] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [rollbackReason, setRollbackReason] = useState("");

  const loadHistory = async () => {
    if (!settingKey) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(
        `core-v1/application-settings/${settingKey}/history`
      );
      setData(data);
    } catch {
      failedNotification("Failed to load history");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (visible) loadHistory();
  }, [visible]);
  const renderBooleanIcon = (value: boolean | undefined) => {
    if (value) {
      return <CheckOutlined style={{ color: "var(--tertiary-color)" }} />;
    }
    return <CloseOutlined style={{ color: "var(--red)" }} />;
  };

  const rollbackToVersion = async (version: number) => {
    try {
      await apiClient.post(
        `core-v1/application-settings/${settingKey}/rollback`,
        {
          targetVersion: version,
          reason: rollbackReason,
        }
      );
      successfulNotification("Setting rollback successful");
      reloadParent();
      onClose();
    } catch {
      failedNotification("Rollback failed");
    }
  };

  const columns: ColumnsType<HistoryEntry> = [
    {
      title: "Version",
      dataIndex: "version",
      width: 80,
    },
    {
      title: "Value",
      dataIndex: "value",
      render: (v) => (
        <pre style={{ margin: 0 }}>
          {typeof v === "object" ? JSON.stringify(v, null, 2) : String(v)}
        </pre>
      ),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      render: (_, row) => renderBooleanIcon(row.isActive),

    },
    {
      title: "Reason",
      dataIndex: "reason",
    },
    {
      title: "Date",
      dataIndex: "updatedAt",
    },
    {
      title: "Action",
      render: (_, row) => (
        <PermissionWrapper
          hasPermissions={hasEditAccess}
          children={
            <Button
              size="small"
              danger
              onClick={() => rollbackToVersion(row.version)}
            >
              Rollback
            </Button>
          }
        />
      ),
    },
  ];

  return (
    <Drawer
      title={`History: ${settingKey}`}
      open={visible}
      width={700}
      onClose={onClose}
       className="manage-pages-drawer history-app-setting-drawer"
    >
      <Input.TextArea
        rows={2}
        placeholder="Rollback reason (optional)"
        value={rollbackReason}
        onChange={(e) => setRollbackReason(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      <Table
        rowKey="version"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={false}
      />
    </Drawer>
  );
};

export default SettingHistoryDrawer;
